import { Box, Button, Flex, Heading, Select } from '@ritualco/jutsu'
import { CanvasBox } from './CanvasBox'
import { useQRCodeCanvas } from './useQRCodeCanvas'
import { useQRCodeDownload } from './useQRCodeDownload'
import { useContent } from 'lib/content/react'

export type QRCodeProps = {
  url: string
  allowDownload?: boolean
}

const fileExts = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  svg: 'svg',
}
export type FileFormats = keyof typeof fileExts

const fileSizes = [200, 400, 800, 1000] as const
export type FileSizes = typeof fileSizes[number]

export const QRCode = ({ url, allowDownload = true }: QRCodeProps) => {
  const canvasEl = useQRCodeCanvas(url)
  const { formatMessage } = useContent()
  const [
    downloadUrl,
    setDownloadWidth,
    downloadFormat,
    setDownloadFormat,
  ] = useQRCodeDownload(url)

  if (!allowDownload) {
    return <CanvasBox ref={canvasEl} />
  }

  return (
    <Flex justify="space-between" wrap="wrap">
      <CanvasBox
        ref={canvasEl}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex="1"
        mr={4}
        mb={4}
      />

      <Box flex="1" ml={4} minWidth="200px">
        <Heading as="h5" size="lg" mb={4}>
          {formatMessage({ id: 'pmportal.restaurantLists.saveQrCode' })}
        </Heading>
        <label>
          <Heading as="h6" size="sm" mt={3} mb={2}>
            {formatMessage({ id: 'pmportal.restaurantLists.fileFormat' })}
          </Heading>
          <Select
            onChange={e => setDownloadFormat(e.target.value as FileFormats)}
          >
            {Object.entries(fileExts).map(([formatLong, formatShort]) => (
              <option key={formatLong} value={formatLong}>
                {formatShort.toLocaleUpperCase()}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <Heading as="h6" size="sm" mt={3} mb={2}>
            {formatMessage({ id: 'pmportal.restaurantLists.imageSize' })}
          </Heading>
          <Select
            onChange={e =>
              setDownloadWidth(Number(e.target.value) as FileSizes)
            }
          >
            {fileSizes.map(size => (
              <option key={size} value={size}>
                {formatMessage(
                  { id: 'pmportal.restaurantLists.dimensions' },
                  { size }
                )}
              </option>
            ))}
          </Select>
        </label>

        <Flex justify="flex-end" mt={4}>
          <a
            download={`qrcode.${fileExts[downloadFormat]}`}
            href={downloadUrl}
            title={formatMessage({
              id: 'pmportal.restaurantLists.saveImageTitle',
            })}
          >
            <Button variant="solid">
              {formatMessage({ id: 'pmportal.restaurantLists.saveImage' })}
            </Button>
          </a>
        </Flex>
      </Box>
    </Flex>
  )
}
